import React from 'react';
import {
    Box,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
} from '@mui/material';

/* ──────────────────────── helpers ──────────────────────── */

const SkeletonRow = ({ cells, height = 20 }) => (
    <TableRow>
        {cells.map((c, i) => (
            <TableCell key={i} align={c.align || 'left'}>
                <Skeleton variant="text" width={c.width || '80%'} height={height} animation="wave" />
            </TableCell>
        ))}
    </TableRow>
);

const InfoRowSkeleton = ({ labelWidth = 120, valueWidth = 180, align = 'left' }) => (
    <Stack
        direction="row"
        spacing={4}
        justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
        sx={{ mb: 2.5 }}
    >
        <Skeleton variant="text" width={labelWidth} height={22} animation="wave" />
        <Skeleton variant="text" width={valueWidth} height={22} animation="wave" />
    </Stack>
);

/* ──────────────────────── BILL SKELETON ──────────────────────── */

const BillSkeleton = () => (
    <Box>
        {/* Breadcrumb placeholder */}
        <Box sx={{ mb: 3 }}>
            <Skeleton variant="text" width={260} height={28} animation="wave" />
        </Box>

        <Paper
            variant="outlined"
            sx={{
                p: 5,
                borderRadius: 3,
                maxWidth: 1000,
                mx: 'auto',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Ribbon */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 16,
                    left: -8,
                    width: 100,
                    height: 28,
                    transform: 'rotate(-45deg)',
                }}
            >
                <Skeleton variant="rectangular" width={100} height={28} animation="wave" sx={{ borderRadius: 1 }} />
            </Box>

            {/* Title right-aligned */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 2, mb: 6 }}>
                <Skeleton variant="text" width={100} height={48} animation="wave" />
                <Skeleton variant="text" width={160} height={36} animation="wave" />
            </Box>

            {/* Balance Due */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mb: 6 }}>
                <Skeleton variant="text" width={100} height={18} animation="wave" />
                <Skeleton variant="text" width={140} height={36} animation="wave" />
            </Box>

            {/* Info rows */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mb: 4, gap: 1 }}>
                <InfoRowSkeleton align="right" />
                <InfoRowSkeleton align="right" />
                <InfoRowSkeleton align="right" />
            </Box>

            {/* Items table */}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4, borderLeft: 0, borderRight: 0, borderRadius: 0 }}>
                <Table size="medium">
                    <TableHead sx={{ bgcolor: '#fafafa' }}>
                        <TableRow>
                            {['Item', 'Unit', 'Qty', 'Unit Price', 'Total Price'].map((h, i) => (
                                <TableCell key={i} align={i >= 2 ? 'right' : 'left'}>
                                    <Skeleton variant="text" width={i === 0 ? 60 : 70} height={22} animation="wave" />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[0, 1, 2].map((r) => (
                            <SkeletonRow
                                key={r}
                                cells={[
                                    { width: '70%' },
                                    { width: '50%' },
                                    { width: '40%', align: 'right' },
                                    { width: '60%', align: 'right' },
                                    { width: '60%', align: 'right' },
                                ]}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Footer totals */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
                <Box sx={{ width: 260 }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Stack key={i} direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
                            <Skeleton variant="text" width={90} height={20} animation="wave" />
                            <Skeleton variant="text" width={80} height={20} animation="wave" />
                        </Stack>
                    ))}
                </Box>
            </Box>
        </Paper>
    </Box>
);

/* ──────────────────────── PAYMENT SKELETON ──────────────────────── */

const PaymentSkeleton = () => (
    <Box>
        {/* Breadcrumb */}
        <Box sx={{ mb: 3 }}>
            <Skeleton variant="text" width={300} height={28} animation="wave" />
        </Box>

        <Paper
            variant="outlined"
            sx={{
                p: 5,
                borderRadius: 3,
                maxWidth: 1000,
                mx: 'auto',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Ribbon */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 16,
                    left: -8,
                    width: 100,
                    height: 28,
                    transform: 'rotate(-45deg)',
                }}
            >
                <Skeleton variant="rectangular" width={100} height={28} animation="wave" sx={{ borderRadius: 1 }} />
            </Box>

            {/* Centered title */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5, mt: 1 }}>
                <Skeleton variant="text" width={200} height={40} animation="wave" />
            </Box>

            {/* Info + Amount box */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={7}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Stack key={i} direction="row" spacing={2} sx={{ mb: 2.5 }}>
                            <Skeleton variant="text" width={150} height={22} animation="wave" />
                            <Skeleton variant="text" width={200} height={22} animation="wave" sx={{ flex: 1 }} />
                        </Stack>
                    ))}
                </Grid>
                <Grid
                    item xs={12} md={5}
                    sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'flex-start' }}
                >
                    <Skeleton
                        variant="rectangular"
                        width={200}
                        height={90}
                        animation="wave"
                        sx={{ borderRadius: 2 }}
                    />
                </Grid>
            </Grid>

            {/* Paid To */}
            <Box sx={{ mb: 4 }}>
                <Skeleton variant="text" width={60} height={18} animation="wave" />
                <Skeleton variant="text" width={180} height={28} animation="wave" />
            </Box>

            <Skeleton variant="rectangular" width="100%" height={1} sx={{ mb: 4 }} />

            {/* Payment-for table */}
            <Box sx={{ bgcolor: '#fafafa', borderRadius: 2, p: 3 }}>
                <Skeleton variant="text" width={120} height={28} animation="wave" sx={{ mb: 3 }} />

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, boxShadow: 'none' }}>
                    <Table size="medium">
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                {['Bill Number', 'Bill Date', 'Bill Amount', 'Payment Amount'].map((h, i) => (
                                    <TableCell key={i} align={i >= 2 ? 'right' : 'left'}>
                                        <Skeleton variant="text" width={90} height={22} animation="wave" />
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[0, 1, 2].map((r) => (
                                <SkeletonRow
                                    key={r}
                                    cells={[
                                        { width: '65%' },
                                        { width: '55%' },
                                        { width: '50%', align: 'right' },
                                        { width: '50%', align: 'right' },
                                    ]}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Paper>
    </Box>
);

/* ──────────────────────── PURCHASE SKELETON ──────────────────────── */

const PurchaseSkeleton = () => (
    <Box>
        {/* Breadcrumb */}
        <Box sx={{ mb: 3 }}>
            <Skeleton variant="text" width={280} height={28} animation="wave" />
        </Box>

        <Paper
            variant="outlined"
            sx={{
                p: 5,
                borderRadius: 3,
                maxWidth: 1000,
                mx: 'auto',
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
                <Skeleton variant="text" width={280} height={38} animation="wave" />
            </Box>

            {/* Info rows */}
            <Box sx={{ mb: 5 }}>
                {[1, 2, 3, 4].map((i) => (
                    <Stack key={i} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Skeleton variant="text" width={140} height={22} animation="wave" />
                        {i === 1 ? (
                            <Skeleton variant="rectangular" width={90} height={28} animation="wave" sx={{ borderRadius: '20px' }} />
                        ) : (
                            <Skeleton variant="text" width={180} height={22} animation="wave" />
                        )}
                    </Stack>
                ))}
            </Box>

            {/* Items table */}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4, borderLeft: 0, borderRight: 0, borderRadius: 0 }}>
                <Table size="medium">
                    <TableHead sx={{ bgcolor: '#fafafa' }}>
                        <TableRow>
                            {['Item', 'Unit', 'Qty', 'Unit Price', 'Amount'].map((h, i) => (
                                <TableCell key={i} align={i >= 2 ? 'right' : 'left'}>
                                    <Skeleton variant="text" width={i === 0 ? 50 : 65} height={22} animation="wave" />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[0, 1, 2].map((r) => (
                            <SkeletonRow
                                key={r}
                                cells={[
                                    { width: '70%' },
                                    { width: '50%' },
                                    { width: '40%', align: 'right' },
                                    { width: '60%', align: 'right' },
                                    { width: '60%', align: 'right' },
                                ]}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Footer totals */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
                <Box sx={{ width: 260 }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Stack key={i} direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
                            <Skeleton variant="text" width={80} height={20} animation="wave" />
                            <Skeleton variant="text" width={70} height={i === 4 ? 26 : 20} animation="wave" />
                        </Stack>
                    ))}
                </Box>
            </Box>

            {/* Notes */}
            <Box sx={{ mt: 5 }}>
                <Skeleton variant="text" width={50} height={22} animation="wave" />
                <Skeleton variant="text" width="40%" height={20} animation="wave" sx={{ mt: 1 }} />
            </Box>
        </Paper>
    </Box>
);

/* ──────────────────────── SINGLE PAYMENT FORM SKELETON ──────────────────────── */

const SinglePaymentSkeleton = () => (
    <Box>
        {/* Breadcrumb */}
        <Box sx={{ mb: 3 }}>
            <Skeleton variant="text" width={320} height={28} animation="wave" />
        </Box>

        <Box sx={{ pb: 10 }}>
            <Grid container justifyContent="center">
                <Grid item xs={12} md={8} sx={{ maxWidth: '920px !important' }}>
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        {/* Card title */}
                        <Skeleton variant="text" width={180} height={32} animation="wave" sx={{ mb: 3 }} />

                        {/* Bill quick info */}
                        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                            <Box>
                                <Skeleton variant="text" width={180} height={30} animation="wave" />
                                <Skeleton variant="text" width={140} height={22} animation="wave" />
                            </Box>
                            <Stack direction="row" spacing={3}>
                                {[1, 2, 3].map((i) => (
                                    <Box key={i} sx={{ textAlign: 'right' }}>
                                        <Skeleton variant="text" width={70} height={14} animation="wave" sx={{ ml: 'auto' }} />
                                        <Skeleton variant="text" width={90} height={28} animation="wave" sx={{ ml: 'auto' }} />
                                    </Box>
                                ))}
                            </Stack>
                        </Box>

                        {/* Divider */}
                        <Skeleton variant="rectangular" width="100%" height={1} sx={{ mb: 4 }} />

                        <Grid container spacing={3}>
                            {/* Amount to Pay */}
                            <Grid item xs={12}>
                                <Skeleton variant="text" width={120} height={24} animation="wave" sx={{ mb: 1 }} />
                                <Skeleton variant="rectangular" width="100%" height={48} animation="wave" sx={{ borderRadius: 1 }} />
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                                    <Skeleton variant="rectangular" width={20} height={20} animation="wave" sx={{ borderRadius: 0.5 }} />
                                    <Skeleton variant="text" width={180} height={20} animation="wave" />
                                </Stack>
                            </Grid>

                            {/* Payment Method + Date */}
                            <Grid item xs={12} md={6}>
                                <Skeleton variant="text" width={130} height={24} animation="wave" sx={{ mb: 1 }} />
                                <Skeleton variant="rectangular" width="100%" height={42} animation="wave" sx={{ borderRadius: 1 }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Skeleton variant="text" width={110} height={24} animation="wave" sx={{ mb: 1 }} />
                                <Skeleton variant="rectangular" width="100%" height={42} animation="wave" sx={{ borderRadius: 1 }} />
                            </Grid>

                            {/* Reference */}
                            <Grid item xs={12}>
                                <Skeleton variant="text" width={100} height={24} animation="wave" sx={{ mb: 1 }} />
                                <Skeleton variant="rectangular" width="100%" height={42} animation="wave" sx={{ borderRadius: 1 }} />
                            </Grid>

                            {/* Notes */}
                            <Grid item xs={12}>
                                <Skeleton variant="text" width={50} height={24} animation="wave" sx={{ mb: 1 }} />
                                <Skeleton variant="rectangular" width="100%" height={80} animation="wave" sx={{ borderRadius: 1 }} />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>

        {/* Sticky footer bar */}
        <Box
            sx={{
                position: 'fixed',
                bottom: 0,
                left: { xs: 0, lg: 240 },
                right: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(8px)',
                borderTop: '1px solid',
                borderColor: 'divider',
                p: 2,
                zIndex: 1100,
                display: 'flex',
                justifyContent: 'center',
                boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
            }}
        >
            <Stack direction="row" spacing={2} sx={{ width: '100%', maxWidth: 920, px: 2 }}>
                <Skeleton variant="rectangular" width={120} height={38} animation="wave" sx={{ borderRadius: 1 }} />
                <Box sx={{ flexGrow: 1 }} />
                <Skeleton variant="rectangular" width={150} height={38} animation="wave" sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={150} height={38} animation="wave" sx={{ borderRadius: 1 }} />
            </Stack>
        </Box>
    </Box>
);

/* ──────────────────────── MAIN COMPONENT ──────────────────────── */

const VARIANTS = {
    bill: BillSkeleton,
    payment: PaymentSkeleton,
    purchase: PurchaseSkeleton,
    singlePayment: SinglePaymentSkeleton,
};

const DetailPageSkeleton = ({ variant = 'bill' }) => {
    const Component = VARIANTS[variant] || BillSkeleton;
    return <Component />;
};

export default DetailPageSkeleton;
